import { useEffect, useMemo, useRef, useState } from "react"
import { useDebounce } from "use-debounce"
import { Box, CloseButton, Dialog, InputGroup, Input, Portal, Stack, Separator } from "@chakra-ui/react"
import { Deal } from "@/types"
import { DealListItem } from "../shared"

interface Props {
  deals: Deal[]
  isOpen: boolean
  onClose: () => void
}

const PAGE_SIZE = 50

export const SearchClientsModal = ({ deals, isOpen, onClose }: Props) => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch] = useDebounce(searchText, 300);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!loadMoreRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, deals.length)
          )
        }
      },
      {
        threshold: 1
      }
    )

    observer.observe(loadMoreRef.current)

    return () => observer.disconnect()
  }, [deals.length])

  const normalize = (value: string) =>
    value.toLowerCase().trim().replace(/\s+/g, " ");

  const filteredDeals = useMemo(() => {
    if (!debouncedSearch) return deals;

    const search = normalize(debouncedSearch);

    return deals.filter(deal => {
      const firstName = normalize(deal.client.firstName);
      const lastName = normalize(deal.client.lastName);
      const fullName = `${firstName} ${lastName}`;
      const reverseFullName = `${lastName} ${firstName}`;

      const matchesName =
        firstName.includes(search) ||
        lastName.includes(search) ||
        fullName.includes(search) ||
        reverseFullName.includes(search);

      const matchesEmail = normalize(deal.client.email).includes(search);

      const matchesCompany = normalize(deal.company.name).includes(search);

      return matchesName || matchesEmail || matchesCompany;
    });
  }, [deals, debouncedSearch]);

  const visibleDeals = filteredDeals.slice(0, visibleCount)

  useEffect(() => {
    if (!isOpen) {
      setSearchText("");
    }
  }, [isOpen])

  return (
    <Dialog.Root size={{ base: "full", md: "xl" }} open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Body borderRadius="10" p={0} border="1px solid" borderColor="whiteAlpha.200">
              <Stack h="80vh" w="100%" gap={0}>
                <InputGroup w="100%" h={{ base: "70px", md: "100px" }}>
                  <Input
                    m={5}
                    variant="subtle"
                    bg="transparent"
                    fontSize={{ base: "md", md: "xl" }}
                    placeholder="Search deals by client name, email, or company..."
                    fontFamily="body"
                    focusRingColor="transparent"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </InputGroup>
                <Separator mb={5} />
                <Stack w="100%" h="calc(100% - 101px)" overflowY="auto" px={5} borderTop="1px solid" borderColor="whiteAlpha.200">
                  {
                    visibleDeals.map((deal) => (
                      <DealListItem key={deal.id} deal={deal} />
                    ))
                  }

                  {visibleCount < deals.length && (
                    <Box ref={loadMoreRef} height="40px" />
                  )}
                </Stack>
              </Stack>
            </Dialog.Body>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
